import dayjs from 'dayjs';
import type { Facility, FacilityCertificate, Supplier } from 'features/suppliers/types';
import { CERTIFICATE_INSTITUTE_RECORDS } from './certificateInstitutes';
import { CERTIFICATE_STANDARD_RECORDS } from './certificateStandards';
import {
  facilitiesDataCache,
  getOrgSuppliers,
  type MockFacility,
} from 'mocks/api/handlers/facilitiesCache';
import { getSuppliersData } from 'mocks/api/handlers/suppliersCache';

/**
 * Deterministic, additive seed for DL-65060 (D2 Playwright automation — "Create Certificate"
 * Testmo folder, 13 cases, group_id=208182). Mirrors the shape of `facilityCertificatesCrudFixture.ts`
 * (dl-63719) and `facilityCertificatesLazyLoadFixture.ts` (DL-64870) — a dedicated sub-supplier +
 * facility, so this epic's created certificates never contend with those sibling epics' fixtures or
 * row-count assertions. This module only ADDS a sub-supplier/facility; it never mutates existing data.
 *
 * The seeded facility carries ONE pre-existing certificate (`baselineCertId`) whose validity window
 * comfortably spans "today" (status Valid) and which has an Institute set — this is the negative
 * control several stories need: "a certificate whose range includes today reads Valid in the same
 * table" (TC-CR-005/010/011) and "a certificate WITH an institute in the same table, so a blanket
 * -blank Institute column is distinguishable" (TC-CR-004).
 *
 * Reused instead of duplicated: this epic's own `CERTIFICATE_STANDARD_RECORDS` (already disjoint by
 * `scope: 'facility' | 'product'`, deterministically faker-seeded — resolves epic divergence 3/7, no
 * new standards seed needed) and `CERTIFICATE_INSTITUTE_RECORDS`.
 */

/** Supplier-portal org id every supplier-persona e2e test resolves to (see `mocks/api/handlers/auth.ts`). */
export const CERTIFICATE_CREATE_EPIC_ORG_ID = 63;

export const CERTIFICATE_CREATE_EPIC_FIXTURE = {
  subSupplierId: 'dl65060sub',
  subSupplierName: 'DL-65060 Certificate Subsupplier',
  facilityId: 'dl65060fac',
  facilityName: 'DL-65060 Certificate Facility',
  baselineCertId: 'dl65060-baseline',
  /** The seeded baseline certificate's Institute — asserted absent from a fresh "no institute" row. */
  baselineInstituteLabel: CERTIFICATE_INSTITUTE_RECORDS[0]?.label ?? 'Baseline Institute',
  /** A non-first institute option — TC-CR-003 must pick a non-default option to prove real wiring. */
  nonFirstInstituteLabel: CERTIFICATE_INSTITUTE_RECORDS[5]?.label ?? 'Secondary Institute',
  /**
   * A pre-seeded, already-expired certificate (both dates in the past). TC-CR-011 asks for an
   * Expired status on CREATE, but the shipped Yup validation
   * (`certificateValidityDatesYupShape.validityTo`) requires ValidTo to be strictly in the future
   * regardless of ValidFrom — an already-expired certificate can never be created through the form
   * (a genuine divergence, recorded on the epic/PR rather than worked around). This row exists so
   * the STATUS DERIVATION itself (`getExpiryStatusAndColor`) can still be verified against a
   * doubly-past-dated certificate, independent of the create path.
   */
  expiredCertId: 'dl65060-expired',
  expiredCertNumber: 'DL65060-EXPIRED',
} as const;

const baselineFacilityStandard = CERTIFICATE_STANDARD_RECORDS.find(r => r.scope === 'facility');

const makeBaselineCert = (): FacilityCertificate => ({
  id: CERTIFICATE_CREATE_EPIC_FIXTURE.baselineCertId,
  certificateNumber: 'DL65060-BASELINE',
  standard: {
    id: baselineFacilityStandard?.apiId ?? 950_900,
    name: baselineFacilityStandard?.label ?? 'Baseline Facility Standard',
    certificateType: 'facility',
  },
  institute: {
    id: 950_901,
    name: CERTIFICATE_CREATE_EPIC_FIXTURE.baselineInstituteLabel,
  },
  status: 'valid',
  // Comfortably spans "today" on either side so it reads Valid regardless of when the suite runs.
  validFrom: dayjs().subtract(300, 'day').toISOString(),
  validTo: dayjs().add(300, 'day').toISOString(),
});

const makeExpiredCert = (): FacilityCertificate => ({
  id: CERTIFICATE_CREATE_EPIC_FIXTURE.expiredCertId,
  certificateNumber: CERTIFICATE_CREATE_EPIC_FIXTURE.expiredCertNumber,
  standard: {
    id: baselineFacilityStandard?.apiId ?? 950_900,
    name: baselineFacilityStandard?.label ?? 'Baseline Facility Standard',
    certificateType: 'facility',
  },
  institute: { id: 950_902, name: CERTIFICATE_CREATE_EPIC_FIXTURE.nonFirstInstituteLabel },
  status: 'expired',
  validFrom: dayjs().subtract(600, 'day').toISOString(),
  validTo: dayjs().subtract(240, 'day').toISOString(),
});

const makeFacility = (): MockFacility => {
  const base: Facility = {
    id: CERTIFICATE_CREATE_EPIC_FIXTURE.facilityId,
    organizationId: CERTIFICATE_CREATE_EPIC_ORG_ID,
    organizationSubSupplierId: CERTIFICATE_CREATE_EPIC_FIXTURE.subSupplierId,
    name: CERTIFICATE_CREATE_EPIC_FIXTURE.facilityName,
    address: '65060 Testmo Way',
    zipCode: '2100',
    city: 'Copenhagen',
    stateOrRegion: 'Capital Region',
    countryCode: 'DK',
    latitude: 55.6761,
    longitude: 12.5683,
    website: 'https://example.com',
    vatNumber: 'DK65060065',
    mid: 'MID-65060',
    relationType: 1,
    processes: [],
    isArchived: false,
    isInUse: true,
    certificateCount: 2,
  };
  return { ...base, facilityCertificates: [makeBaselineCert(), makeExpiredCert()] };
};

const makeSupplier = (): Supplier => ({
  id: CERTIFICATE_CREATE_EPIC_FIXTURE.subSupplierId,
  organizationId: CERTIFICATE_CREATE_EPIC_ORG_ID,
  name: CERTIFICATE_CREATE_EPIC_FIXTURE.subSupplierName,
  countryCode: 'DK',
  city: 'Copenhagen',
  address: '65060 Testmo Way',
  relationType: 1,
  avatarColor: '#2E7D32',
  archived: false,
  isArchived: false,
  isSupplierInUse: true,
  contacts: [],
  facilities: [],
});

let seeded = false;

export const seedCertificateCreateEpicFixture = (): void => {
  if (seeded) return;
  seeded = true;

  const subSuppliersList = getSuppliersData(CERTIFICATE_CREATE_EPIC_ORG_ID);
  if (!subSuppliersList.some(s => s.id === CERTIFICATE_CREATE_EPIC_FIXTURE.subSupplierId)) {
    subSuppliersList.unshift(makeSupplier());
  }
  const orgSuppliers = getOrgSuppliers(CERTIFICATE_CREATE_EPIC_ORG_ID);
  if (!orgSuppliers.some(s => s.id === CERTIFICATE_CREATE_EPIC_FIXTURE.subSupplierId)) {
    orgSuppliers.unshift(makeSupplier());
  }

  facilitiesDataCache.set(CERTIFICATE_CREATE_EPIC_FIXTURE.subSupplierId, [makeFacility()]);
};
